import { useState, useEffect } from "react";
import { colType, ClocksListResponse } from "../../../Types";
import MsTable from "../../../components/Widgets/Table";
import CurrTime from "../../../components/Widgets/CurrTime";
import { NavLink } from "react-router-dom";
import { onlineCheck } from "../../../utils/util";
import DeviceModal from "../../../views/Device/components/DeviceModal";
import adminApiClient from "../../../api/admin";
import DSDialog from "../../../views/system-design/DSDialog";
import DSDialogTitle from "../../../views/system-design/DSDialogTitle";
import DSIconButton from "../../../views/system-design/DSIconButton";
import DSBox from "../../../views/system-design/DSBox";
import DSTypography from "../../../views/system-design/DSTypography";
import DSCircularProgress from "../../../views/system-design/DSCircularProgress";
import CloseIcon from "@mui/icons-material/Close";

type UnregisteredDevicesListModalProps = {
  open: boolean;
  onClose: () => void;
};

const PAGE_SIZE = 100;

function UnregisteredDevicesListModal({
  open,
  onClose,
}: UnregisteredDevicesListModalProps) {
  const [devices, setDevices] = useState<ClocksListResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    if (open) {
      // Reset and fetch initial data when modal opens
      setDevices([]);
      setHasMore(true);
      fetchDevices(0);
    }
  }, [open]);

  const fetchDevices = async (skip: number) => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const fetchedDevices = await adminApiClient.getClocks({
        skip,
        limit: PAGE_SIZE,
        query: "",
        registrationStatus: "NOT_REGISTERED",
        fromVersion: 600,
        heartbeatMinutes: 2880, // 2 days = 2 * 24 * 60 = 2880 minutes
      });

      if (skip === 0) {
        setDevices(fetchedDevices);
      } else {
        setDevices((prev) => [...prev, ...fetchedDevices]);
      }

      setHasMore(fetchedDevices.length === PAGE_SIZE);
    } catch (error) {
      console.error("Error fetching unregistered devices:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleScrollDown = () => {
    if (hasMore && !isLoading) {
      fetchDevices(devices.length);
    }
  };

  const tableColumns: Array<colType> = [
    {
      size: 40,
      noHeadline: true,
      sort: false,
      label: "Online",
      field: "online",
    },
    {
      sort: true,
      label: "Device ID",
      field: "idf",
    },
    {
      sort: true,
      label: "Location",
      field: "location",
    },
    {
      sort: true,
      label: "Heartbeat",
      field: "heartbeat",
    },
    {
      size: 90,
      sort: false,
      label: "Clock Time",
      field: "clockTime",
    },
    {
      sort: true,
      label: "Current Klydo id",
      field: "currentIdf",
      size: 300,
    },
    {
      sort: true,
      label: "Registered",
      field: "registerProduct",
    },
    {
      size: 48,
      sort: false,
      label: "Version",
      field: "version",
    },
  ];

  const tableRows = (r: ClocksListResponse, key: number) => {
    const city = r.location?.city ? r.location.city : "no city";
    const country = r.location?.country_code
      ? r.location.country_code
      : "no country";

    return {
      idf: <p title={r.idf}>{r.idf}</p>,
      location: city + ", " + country,
      clockTime: r.clocktime ? (
        <CurrTime time={r.clocktime} offset={undefined}></CurrTime>
      ) : (
        "-no info-"
      ),
      currentIdf: r.klydo && (
        <NavLink
          to="/admin/klydos"
          onClick={() => {
            localStorage.setItem("klydossearch", r.klydo!.id);
          }}
          title={r.klydo?.id}
        >
          {r.klydo?.id}
        </NavLink>
      ),
      heartbeat: new Date(r.heartbeat)?.toLocaleString(undefined, {
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
      }),
      registerProduct: r.registerProduct
        ? new Date(r.registerProduct)?.toLocaleDateString(undefined, {
            year: "numeric",
            month: "numeric",
            day: "numeric",
          })
        : "-no info-",
      version: (
        <NavLink
          to={r.version ? "/admin/versions" : ""}
          onClick={() => {
            if (r.version) {
              localStorage.setItem(
                "versionssearch",
                r.version.toString() || "",
              );
            }
          }}
        >
          {r.version || "-no info-"}
        </NavLink>
      ),
      online: (
        <i
          className="fa fa-circle"
          style={{
            color: onlineCheck(new Date(r.heartbeat)) ? "#4CAF50" : "#F44336",
          }}
        ></i>
      ),
      key: key,
    };
  };

  return (
    <DSDialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        // 80vh only when the dialog floats; auto-fullscreen on phones
        // must keep the paper at full height
        sx: {
          height: { xxs: "100%", sm: "80vh" },
          maxHeight: { xxs: "100%", sm: "80vh" },
        },
      }}
    >
      <DSDialogTitle>
        <DSBox
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <DSTypography variant="h6">
            Unregistered Devices
            {devices.length > 0 && ` (${devices.length}${hasMore ? "+" : ""})`}
          </DSTypography>
          <DSIconButton onClick={onClose} size="small">
            <CloseIcon />
          </DSIconButton>
        </DSBox>
      </DSDialogTitle>
      <DSBox
        sx={{
          height: "100%",
          p: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {devices.length > 0 ? (
          <MsTable
            id="unregisteredDevicesTable"
            rowBuild={tableRows}
            initSort=""
            data={devices}
            cols={tableColumns}
            title=""
            shouldHideSearchBar={false}
            modal={DeviceModal}
            onScrollDown={handleScrollDown}
          />
        ) : isLoading ? (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSCircularProgress />
            <DSTypography>Loading devices...</DSTypography>
          </DSBox>
        ) : (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSTypography>No unregistered devices found</DSTypography>
          </DSBox>
        )}
      </DSBox>
    </DSDialog>
  );
}

export default UnregisteredDevicesListModal;
